import { BulbOutlined, DisconnectOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Col, Modal, Row, Space, Steps, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { CellValue } from '../../elements';
import { Field, Grid, GridCell, GridCellChangeRequest, GridRow } from '../../models';
import { CellStatusIndicator } from './cell-status-indicator';
import { ChangeRequestActionButtons } from './change-request-action-buttons';
import moment from 'moment';
import { GridPage } from './grid-page';
import { CellRequiredFieldIndicator } from './cell-required-field-indicator';
import { CellErrorsIndicator } from './cell-errors-indicator';

export function GridCellContent(props: {
  grid?: Grid;
  field: Field;
  rowId: string;
  cell: GridCell;
  isObsolete: boolean;
  showTasks: boolean;
  hasActiveOperation: boolean;
  isEllipsis: boolean;
  lastUpdateHighlightDate?: Date;
  onOperationStarted: () => void;
  onOperationEnded: () => void;
  onCellClick: (field: Field, rowId: string) => void;
  onSaved: (row: GridRow) => void;
  onSubGridChanged?: () => void;
  fromCardDetails?: boolean;
}) {
  const [cell, setCell] = useState(props.cell);
  const [isSubGridModalVisible, setIsSubGridModalVisible] = useState(false);

  useEffect(() => {
    setCell(props.cell);
  }, [props.cell]);

  const onCellClick = () => {
    if (props.field.dataType === 'SUBGRID') {
      openSubGridModal();
      return;
    }
    props.onCellClick(props.field, props.rowId);
  };

  function openSubGridModal() {
    setIsSubGridModalVisible(true);
  }

  return (
    <>
      <Space direction="vertical" style={{ width: '100%', padding: props.showTasks ? '0.75rem' : '0', fontSize: '0.8rem' }}>
        <div
          onClick={props.field.dataType !== 'FILE_UPLOAD' ? onCellClick : undefined}
          style={{
            display: 'flex',
            flexFlow: 'row nowrap',
            justifyContent: 'space-between',
            cursor: 'pointer',
            padding: props.showTasks ? '0.5rem' : '0',
            backgroundColor: props.showTasks ? '#52c41a30' : 'inherit',
          }}
        >
          <div style={{ flexGrow: 1, overflow: 'hidden' }}>
            <div
              style={{
                minWidth: '1rem',
                display: 'inline-block',
                width: '100%',
                color:
                  props.lastUpdateHighlightDate && cell?.updatedAt && moment(cell?.updatedAt).isAfter(moment(props.lastUpdateHighlightDate))
                    ? '#faad14'
                    : 'inherit',
              }}
            >
              {((!props.fromCardDetails && props.field.showSubGridLinkOnTable) ||
                (props.fromCardDetails && props.field.showSubGridLinkOnCardDetail)) &&
              props.field.dataType === 'SUBGRID' ? (
                <Button type="link" onClick={openSubGridModal} style={{ padding: 0 }}>
                  Tabloya git
                </Button>
              ) : (
                props.field.dataType === 'SUBGRID' && (
                  <SubGrid subGrid={props.grid?.subGrids?.find(g => g.id === props.field.subGridId)} data={cell?.value} />
                )
              )}
              {((!props.field.isWorkFlow && props.field.dataType !== 'SUBGRID') || (props.field.isWorkFlow && props.isEllipsis)) && (
                <CellValue
                  originalValue={cell?.value}
                  cell={cell}
                  field={props.field}
                  isObsolete={props.isObsolete}
                  isEllipsis={props.isEllipsis}
                  picklistImageInline={true}
                />
              )}
              {props.field.isWorkFlow && !props.isEllipsis && (
                <div style={{ paddingBottom: '1rem' }}>
                  <Steps size="small" current={props.field.picklistValues?.findIndex(pv => pv.val === cell?.value) || 0}>
                    {props.field.picklistValues
                      ?.sort((a, b) => a.order - b.order)
                      ?.map((pv, index) => (
                        <Steps.Step key={index} title={pv.val} />
                      ))}
                  </Steps>
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', flexGrow: 0, alignItems: 'center' }}>
            {props.showTasks && props.field.dataType !== 'FILE_UPLOAD' && <EditOutlined style={{ margin: '0 2px' }} />}
            {props.cell?.errors?.findIndex(x => x === 'Zorunlu alan') > -1 && <CellRequiredFieldIndicator />}
            {(props.cell?.errors?.filter(x => x !== 'Onaylanmamış değer').length || 0) > 0 && <CellErrorsIndicator errors={props.cell?.errors} />}
            {cell?.etCount > 0 && <CellStatusIndicator color="lime" count={cell?.etCount} />}
            {!props.showTasks && (cell?.vtCount || 0) > 0 && <CellStatusIndicator color="gold" count={cell?.vtCount} />}
            {!props.showTasks && (cell?.vtCount || 0) <= 0 && cell.tExists && <CellStatusIndicator color="gray" />}
            {props.field.dataType === 'FILE_UPLOAD' && <EditOutlined onClick={onCellClick} />}
          </div>
        </div>
        {props.showTasks && (cell?.vtCount || 0) > 0 && (
          <PendingChanges
            field={props.field}
            valueTasks={cell?.valueTasks}
            canEvaluate={(cell?.vtCount || 0) > 0}
            hasActiveOperation={props.hasActiveOperation}
            onOperationStarted={props.onOperationStarted}
            onOperationEnded={props.onOperationEnded}
            onSaved={props.onSaved}
          />
        )}
      </Space>
      <Modal
        title={props.field.displayName}
        visible={isSubGridModalVisible}
        onCancel={() => setIsSubGridModalVisible(false)}
        footer={null}
        width={1400}
        destroyOnClose={true}
      >
        <GridPage
          gridId={props.field.subGridId}
          rowId={props.rowId}
          onChange={props.field.dataType === 'SUBGRID' ? props.onSubGridChanged : undefined}
        />
      </Modal>
    </>
  );
}

function PendingChanges(props: {
  field?: Field;
  valueTasks?: GridCellChangeRequest[];
  hasActiveOperation: boolean;
  canEvaluate?: boolean;
  onOperationStarted: () => void;
  onOperationEnded: () => void;
  onSaved: (row: GridRow) => void;
}) {
  const { Text } = Typography;

  const [valueTasks, setValueTasks] = useState(props.valueTasks);

  useEffect(() => {
    setValueTasks(props.valueTasks);
  }, [props.valueTasks]);

  if (!valueTasks?.length) {
    return null;
  }

  return (
    <div style={{ padding: '1rem', backgroundColor: '#faad1430', fontSize: '0.75rem' }}>
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Text type="secondary">Bekleyen onaylar</Text>
        {valueTasks?.map(pc => (
          <Row key={pc.id} align="middle" justify="space-between">
            <Col>
              <Text>
                <CellValue cell={pc} originalValue={pc.value} field={props.field} picklistImageInline={true} />
              </Text>
            </Col>
            <Col style={{ textAlign: 'right' }}>
              <ChangeRequestActionButtons
                valueTask={pc}
                hasActiveOperation={props.hasActiveOperation}
                canEvaluate={props.canEvaluate}
                onOperationStarted={props.onOperationStarted}
                onOperationEnded={props.onOperationEnded}
                onSaved={props.onSaved}
                includeTasks={true}
              />
            </Col>
          </Row>
        ))}
      </Space>
    </div>
  );
}

function SubGrid(props: { subGrid?: Grid; data?: any[] }) {
  const { Text } = Typography;

  if (!props.subGrid || !props.data?.length) {
    return null;
  }

  const keys = Object.keys(props.data?.length ? props.data[0] : []);
  const fields = keys.map(key => props.subGrid?.fields?.find(f => f.code === key)).filter(f => !!f) as Field[];
  const systemColumns = keys.filter(k => k.startsWith('MDM_') && k !== 'MDM_IS_PASSIVE' && k !== 'MDM_HAS_CUSTOM_ICON');

  return (
    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      <div style={{ flexGrow: 1 }}>
        <table style={{ border: '1px solid #aaa', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {props.data?.some(r => r['MDM_IS_PASSIVE'] || r['MDM_HAS_CUSTOM_ICON']) && <th></th>}
              {fields?.map(f => (
                <th style={{ border: '1px solid #aaa', padding: '0.25rem' }}>{f.displayName || f.name}</th>
              ))}
              {systemColumns?.length > 0 && (
                <>
                  <th style={{ border: '1px solid #aaa', padding: '0.25rem' }}>Oluşturan</th>
                  <th style={{ border: '1px solid #aaa', padding: '0.25rem' }}>Oluşturma</th>
                  <th style={{ border: '1px solid #aaa', padding: '0.25rem' }}>Son güncelleme</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {props.data?.map(r => (
              <tr>
                {props.data?.some(r => r['MDM_IS_PASSIVE'] || r['MDM_HAS_CUSTOM_ICON']) && (
                  <td style={{ backgroundColor: r['MDM_IS_PASSIVE'] ? '#d69898' : 'inherit' }}>
                    <Space style={{ marginLeft: '0.5rem', marginRight: '0.5rem' }}>
                      {r['MDM_IS_PASSIVE'] && <DisconnectOutlined />}
                      {r['MDM_HAS_CUSTOM_ICON'] && <BulbOutlined />}
                    </Space>
                  </td>
                )}
                {fields?.map(f => (
                  <td
                    style={{ border: '1px solid #aaa', padding: '0.25rem', backgroundColor: r['MDM_IS_PASSIVE'] ? '#d69898' : 'inherit' }}
                  >
                    {r[f.code]}
                  </td>
                ))}
                {systemColumns?.length > 0 && (
                  <>
                    <td
                      style={{ border: '1px solid #aaa', padding: '0.25rem', backgroundColor: r['MDM_IS_PASSIVE'] ? '#d69898' : 'inherit' }}
                    >
                      {r['MDM_CREATED_BY_USER_FULLNAME']}
                    </td>
                    <td
                      style={{ border: '1px solid #aaa', padding: '0.25rem', backgroundColor: r['MDM_IS_PASSIVE'] ? '#d69898' : 'inherit' }}
                    >
                      {moment(r['MDM_CREATED_AT']).format('DD.MM.yyyy HH:mm:ss')}
                    </td>
                    <td
                      style={{ border: '1px solid #aaa', padding: '0.25rem', backgroundColor: r['MDM_IS_PASSIVE'] ? '#d69898' : 'inherit' }}
                    >
                      {moment(r['MDM_UPDATED_AT']).format('DD.MM.yyyy HH:mm:ss')}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ width: '2rem', textAlign: 'center' }}>
        <Text type="secondary">
          <EditOutlined />
        </Text>
      </div>
    </div>
  );
}
